import type { StreamHealthSnapshot } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';

const STORAGE_KEY = 'fm.studio.connectionIssueDays';
const REPEAT_THRESHOLD = 3;

export type ConnectionTriageCause = 'model' | 'fan' | 'platform' | 'unknown';

export interface ConnectionGuidance {
  cause: ConnectionTriageCause;
  title: string;
  body: string;
  isRepeat: boolean;
}

function dayKey(now = new Date()): string {
  return now.toISOString().slice(0, 10);
}

export function recordConnectionIssue(severity: StreamHealthSnapshot['severity']): number {
  if (severity !== 'bad' && severity !== 'warn') {
    return readIssueCountToday();
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as Record<string, number>) : {};
    const key = dayKey();
    const next = { ...parsed, [key]: (parsed[key] ?? 0) + 1 };
    // Keep a short history.
    const trimmed: Record<string, number> = {};
    for (const [day, count] of Object.entries(next).sort().slice(-7)) {
      trimmed[day] = count;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
    return trimmed[key] ?? 0;
  } catch {
    return 0;
  }
}

export function readIssueCountToday(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return 0;
    }
    const parsed = JSON.parse(raw) as Record<string, number>;
    return parsed[dayKey()] ?? 0;
  } catch {
    return 0;
  }
}

/**
 * Heuristic triage — not a perfect root-cause diagnosis (no SFU central stats).
 */
export function triageConnectionHealth(health: StreamHealthSnapshot | undefined): ConnectionGuidance | null {
  if (!health || health.severity === 'ok' || health.severity === 'idle') {
    return null;
  }

  const issueCount = readIssueCountToday();
  const isRepeat = issueCount >= REPEAT_THRESHOLD;
  const modelIssues = health.issues;
  const fanIssues = health.remoteIssues;
  const fansAffected = health.remoteIssueCount;

  let cause: ConnectionTriageCause = 'unknown';
  if (modelIssues.length > 0 && fansAffected >= 2) {
    cause = 'model';
  } else if (modelIssues.length === 0 && fansAffected === 1) {
    cause = 'fan';
  } else if (modelIssues.length === 0 && fansAffected >= 2) {
    cause = 'platform';
  } else if (modelIssues.includes('disconnected') || modelIssues.includes('low-bandwidth')) {
    cause = 'model';
  } else if (fanIssues.length > 0 && modelIssues.length === 0) {
    cause = 'fan';
  }

  const titleKey =
    cause === 'model'
      ? 'common.studio.live.connGuideModelTitle'
      : cause === 'fan'
        ? 'common.studio.live.connGuideFanTitle'
        : cause === 'platform'
          ? 'common.studio.live.connGuidePlatformTitle'
          : 'common.studio.live.connGuideUnknownTitle';
  const bodyKey = isRepeat
    ? 'common.studio.live.connGuideRepeatBody'
    : cause === 'model'
      ? 'common.studio.live.connGuideModelBody'
      : cause === 'fan'
        ? 'common.studio.live.connGuideFanBody'
        : cause === 'platform'
          ? 'common.studio.live.connGuidePlatformBody'
          : 'common.studio.live.connGuideUnknownBody';

  return {
    cause,
    title: t(titleKey),
    body: t(bodyKey),
    isRepeat,
  };
}
